import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { GridContext } from '../Grid';

const gridCellVariants = cva('flex items-center gap-2.5 py-0 px-2 flex-shrink-0', {
  variants: {
    spacing: {
      cozy: 'h-8',
      comfy: 'h-10',
      roomy: 'h-13',
    },
  },
  defaultVariants: {
    spacing: 'cozy',
  },
});

type GridCellProps = React.ComponentProps<'td'> & VariantProps<typeof gridCellVariants> & { asChild?: boolean };

const GridCell = React.forwardRef<HTMLTableCellElement, GridCellProps>(
  ({ className, spacing, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'td';
    const gridContext = React.useContext(GridContext);
    const effectiveSpacing = spacing || gridContext.spacing || 'cozy';

    return (
      <Comp
        ref={ref}
        role="cell"
        data-slot="grid-cell"
        data-spacing={effectiveSpacing}
        className={cn(
          gridCellVariants({ spacing: effectiveSpacing, className }),
          effectiveSpacing === 'cozy' ? 'whitespace-nowrap' : '', // Keep cozy cell spacing to single line of text
        )}
        {...props}
      />
    );
  },
);

GridCell.displayName = 'GridCell';

export { GridCell, type GridCellProps };
